/**
 * Provider-independent errors for the RemoteStore port.
 *
 * The code set is what the policy layer branches on — `needs_sign_in` pauses
 * the upload queue, `not_found` means "gone, re-upload it", `conflict` means
 * "someone else wrote this". Every adapter maps its own HTTP statuses onto
 * these, so `backup.ts` and friends never learn which backend they are on.
 *
 * Messages are Swedish sentences naming the provider, because that is what the
 * user sees; `technical` carries the raw status for "Tekniska detaljer".
 */

export type RemoteErrorCode =
  | 'needs_sign_in'
  | 'forbidden'
  | 'not_found'
  | 'rate_limited'
  | 'conflict'
  | 'bad_request'
  | 'network'
  | 'server'
  | 'other';

/** HTTP status → code. Adapters may override for provider-specific bodies. */
export function codeForStatus(status: number): RemoteErrorCode {
  if (status === 401) return 'needs_sign_in';
  if (status === 403) return 'forbidden';
  if (status === 404 || status === 410) return 'not_found';
  if (status === 429) return 'rate_limited';
  if (status === 409 || status === 412) return 'conflict';
  if (status === 400) return 'bad_request';
  if (status === 0) return 'network';
  if (status >= 500) return 'server';
  return 'other';
}

/** Generic Swedish wording; an adapter passes `messages` to override any of it. */
export function defaultMessages(provider: string): Record<RemoteErrorCode, string> {
  return {
    needs_sign_in: `Inloggning i ${provider} krävs. Logga in igen för att fortsätta.`,
    forbidden: `${provider} nekade åtkomst.`,
    not_found: `Filen finns inte längre i ${provider}.`,
    rate_limited: `${provider} är tillfälligt upptaget. Försök igen om en stund.`,
    conflict: `Filen ändrades i ${provider} under tiden. Försök igen.`,
    bad_request: `${provider} förstod inte begäran.`,
    network: `Ingen kontakt med ${provider}. Kontrollera nätverket och försök igen.`,
    server: `${provider} svarade med ett fel. Försök igen om en stund.`,
    other: `Något gick fel mot ${provider}.`,
  };
}

export interface RemoteStoreErrorInit {
  status: number;
  /** Defaults to `codeForStatus(status)`. */
  code?: RemoteErrorCode | undefined;
  /** Shown under "Tekniska detaljer"; defaults to the status line. */
  technical?: string | undefined;
  /** Honoured by the adapter's own retry loop. */
  retryAfterMs?: number | undefined;
  /** Provider label for the Swedish message, e.g. "Google Drive". */
  provider: string;
  /** Per-code overrides so an adapter can keep its own wording. */
  messages?: Partial<Record<RemoteErrorCode, string>> | undefined;
}

/** Swedish message for the UI, `code` for the policy layer, `technical` for the disclosure. */
export class RemoteStoreError extends Error {
  readonly code: RemoteErrorCode;
  readonly status: number;
  readonly technical: string;
  readonly provider: string;
  readonly retryAfterMs?: number;

  constructor(init: RemoteStoreErrorInit) {
    const code = init.code ?? codeForStatus(init.status);
    super(init.messages?.[code] ?? defaultMessages(init.provider)[code]);
    this.name = 'RemoteStoreError';
    this.code = code;
    this.status = init.status;
    this.provider = init.provider;
    this.technical = init.technical ?? `HTTP ${init.status}`;
    if (init.retryAfterMs !== undefined) this.retryAfterMs = init.retryAfterMs;
  }
}

/** `isRemoteError(e, 'needs_sign_in')` — true for any adapter's errors. */
export function isRemoteError(e: unknown, code?: RemoteErrorCode): e is RemoteStoreError {
  return e instanceof RemoteStoreError && (code === undefined || e.code === code);
}

/** Retryable by the adapter: transient, and the request can be repeated as-is. */
export function isTransient(e: RemoteStoreError): boolean {
  return e.code === 'rate_limited' || e.code === 'server' || e.code === 'network';
}
